import React, { useEffect, useRef, useState } from 'react';
import qbLogo from '../../assets/quickbooks-icon.png';
import API_BASE from '../../utils/api.js';
import { describeQuickBooksDisconnect } from '../../utils/quickbooksDisconnect.js';

export default function QuickBooksSettings() {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');

  const [connected, setConnected] = useState(false);
  // When/why a previous connection was cleared — null if it never existed.
  const [disconnect, setDisconnect] = useState(null);
  const [qbUser, setQbUser] = useState(null);
  const [qbEnvironment, setQbEnvironment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [autoCreateOnSave, setAutoCreateOnSave] = useState(false);
  const [savingAutoCreate, setSavingAutoCreate] = useState(false);
  const [syncStartDate, setSyncStartDate] = useState('');
  const [savedSyncStartDate, setSavedSyncStartDate] = useState('');
  const [savingSyncStartDate, setSavingSyncStartDate] = useState(false);
  const [syncStartDateMessage, setSyncStartDateMessage] = useState('');
  const syncStartDateLoadedRef = useRef(false);
  const [statusUnauthorized, setStatusUnauthorized] = useState(false);
  const [liabilityAccounts, setLiabilityAccounts] = useState([]);
  const [loadingLiabilityAccounts, setLoadingLiabilityAccounts] = useState(false);
  const [selectedLiabilityAccountId, setSelectedLiabilityAccountId] = useState('');
  const [manualLiabilityAccountName, setManualLiabilityAccountName] = useState('');
  const [savingLiabilityAccount, setSavingLiabilityAccount] = useState(false);
  const [backfillingPoPayments, setBackfillingPoPayments] = useState(false);
  const [backfillMessage, setBackfillMessage] = useState('');
  const [resettingLinks, setResettingLinks] = useState(false);
  const [resetLinksMessage, setResetLinksMessage] = useState('');

  const getToken = () => localStorage.getItem('idToken');

  function normalizeAccountText(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isOtherCurrentLiabilityAccount(account) {
    return normalizeAccountText(account?.accountType).includes('other current liabilit');
  }

  function isPreferredRetainerLiabilityAccount(account) {
    const haystack = [
      account?.name,
      account?.fullyQualifiedName,
      account?.accountSubType,
      account?.accountType,
      account?.classification,
    ]
      .map(normalizeAccountText)
      .filter(Boolean)
      .join(' ');

    return (
      isOtherCurrentLiabilityAccount(account) &&
      (
        haystack.includes('retainer') ||
        haystack.includes('deposit') ||
        haystack.includes('prepayment') ||
        haystack.includes('prepayments') ||
        haystack.includes('deferred revenue') ||
        haystack.includes('deferredrevenue') ||
        haystack.includes('deferred income') ||
        haystack.includes('deferredincome') ||
        haystack.includes('unearned')
      )
    );
  }

  function scoreRetainerLiabilityAccount(account) {
    const accountType = normalizeAccountText(account?.accountType);
    const accountSubType = normalizeAccountText(account?.accountSubType);
    const classification = normalizeAccountText(account?.classification);
    const label = normalizeAccountText(account?.fullyQualifiedName || account?.name);
    const haystack = `${accountType} ${accountSubType} ${classification} ${label}`;

    let score = 0;
    if (accountType.includes('other current liabilit')) score += 50;
    if (classification.includes('liabilit')) score += 20;
    if (
      haystack.includes('retainer') ||
      haystack.includes('deposit') ||
      haystack.includes('prepayment') ||
      haystack.includes('prepayments') ||
      haystack.includes('deferred revenue') ||
      haystack.includes('deferredrevenue') ||
      haystack.includes('deferred income') ||
      haystack.includes('deferredincome') ||
      haystack.includes('unearned')
    ) {
      score += 100;
    }
    if (
      haystack.includes('accounts payable') ||
      haystack.includes('tax payable') ||
      haystack.includes('sales tax') ||
      haystack.includes('loan payable') ||
      haystack.includes('notes payable') ||
      haystack.includes('credit card') ||
      haystack.includes('mastercard') ||
      haystack.includes('visa')
    ) {
      score -= 40;
    }

    return score;
  }

  function getDisplayedLiabilityAccounts(accounts) {
    const allAccounts = Array.isArray(accounts) ? accounts : [];
    const preferredAccounts = allAccounts.filter(isPreferredRetainerLiabilityAccount);
    if (preferredAccounts.length) {
      return preferredAccounts;
    }

    const otherCurrentLiabilityAccounts = allAccounts.filter(isOtherCurrentLiabilityAccount);
    if (otherCurrentLiabilityAccounts.length) {
      return otherCurrentLiabilityAccounts;
    }

    return allAccounts.filter((account) => {
      const accountType = normalizeAccountText(account?.accountType);
      const classification = normalizeAccountText(account?.classification);
      return accountType.includes('liabilit') || classification.includes('liabilit');
    });
  }

  async function fetchLiabilityAccounts() {
    const token = getToken();
    if (!token) return;

    setLoadingLiabilityAccounts(true);
    try {
      const res = await fetch(`${apiUrl}/api/quickbooks/accounts?classification=liability&purpose=retainer`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error('Failed to load QuickBooks liability accounts.');
      }

      const data = await res.json();
      const accounts = Array.isArray(data) ? data : [];
      const sorted = [...accounts].sort((left, right) => {
        const scoreDiff =
          scoreRetainerLiabilityAccount(right) - scoreRetainerLiabilityAccount(left);
        if (scoreDiff !== 0) return scoreDiff;
        return String(left.fullyQualifiedName || left.name).localeCompare(
          String(right.fullyQualifiedName || right.name)
        );
      });
      setLiabilityAccounts(sorted);
    } catch (err) {
      console.error('Failed to fetch QuickBooks liability accounts:', err);
      setLiabilityAccounts([]);
    } finally {
      setLoadingLiabilityAccounts(false);
    }
  }

  /* --------------------------------------------------------
     FETCH CONNECTION STATUS
  -------------------------------------------------------- */
  async function fetchStatus() {
    const token = getToken();
    if (!token) {
      setConnected(false);
      setQbUser(null);
      setLiabilityAccounts([]);
      setStatusUnauthorized(true);
      setLoading(false);
      return;
    }

    try {
      const [statusRes, settingsRes] = await Promise.all([
        fetch(`${apiUrl}/api/quickbooks/status`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        userId ? fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}`) : Promise.resolve(null),
      ]);

      if (settingsRes?.ok) {
        const settings = await settingsRes.json();
        setAutoCreateOnSave(Boolean(settings?.quickbooks?.autoCreateOnSave));
        const configuredSyncStartDate = settings?.quickbooks?.syncStartDate || '';
        setSavedSyncStartDate(configuredSyncStartDate);
        // This poll re-reads settings every few seconds; seed the input only
        // once so it never overwrites a date someone is in the middle of picking.
        if (!syncStartDateLoadedRef.current) {
          syncStartDateLoadedRef.current = true;
          setSyncStartDate(configuredSyncStartDate);
        }
        const configuredLiabilityAccountId = settings?.quickbooks?.retainerLiabilityAccountId || '';
        const configuredLiabilityAccountName = settings?.quickbooks?.retainerLiabilityAccountName || '';
        setSelectedLiabilityAccountId(configuredLiabilityAccountId);
        setManualLiabilityAccountName(configuredLiabilityAccountId ? '' : configuredLiabilityAccountName);
      }

      if (statusRes.status === 401) {
        setConnected(false);
        setQbUser(null);
        setLiabilityAccounts([]);
        setStatusUnauthorized(true);
        setLoading(false);
        return;
      }

      if (!statusRes.ok) {
        setConnected(false);
        setQbUser(null);
        setLiabilityAccounts([]);
        setLoading(false);
        return;
      }

      const data = await statusRes.json();

      if (!data.connected) {
        setConnected(false);
        setDisconnect(data.disconnect || null);
        setQbUser(null);
        setQbEnvironment(data.environment || null);
        setLiabilityAccounts([]);
        setLoading(false);
        return;
      }

      setConnected(true);
      setDisconnect(null);
      setQbUser(data.qbUser || null);
      setQbEnvironment(data.environment || null);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch QuickBooks status:', err);
      setConnected(false);
      setQbUser(null);
      setLoading(false);
    }
  }

  /* --------------------------------------------------------
     POLLING
  -------------------------------------------------------- */
  useEffect(() => {
    if (statusUnauthorized) return undefined;

    fetchStatus();

    const interval = setInterval(fetchStatus, connecting ? 2500 : 6000);
    return () => clearInterval(interval);
  }, [connecting, statusUnauthorized]);

  useEffect(() => {
    if (!connected) return;
    fetchLiabilityAccounts();
  }, [connected]);

  /* --------------------------------------------------------
     CONNECT
  -------------------------------------------------------- */
  function handleConnect() {
    setStatusUnauthorized(false);
    setConnecting(true);

    // The account owner's id (what `userId` holds for team seats too): seats
    // share the owner's QuickBooks connection rather than having their own.
    if (!getToken() || !userId) {
      setConnecting(false);
      return;
    }

    window.open(`${apiUrl}/api/quickbooks/connect?userId=${encodeURIComponent(userId)}`, '_blank');

    // The status poll above runs every 2.5s while `connecting`; after 20s it
    // drops back to its normal pace. (A separate interval used to live here,
    // but it read a stale `connected` and so never stopped polling.)
    setTimeout(() => {
      setConnecting(false);
    }, 20000);
  }

  /* --------------------------------------------------------
     DISCONNECT
  -------------------------------------------------------- */
  async function handleDisconnect() {
    try {
      await fetch(`${apiUrl}/api/quickbooks/disconnect`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
    } catch (err) {
      console.error('Failed to disconnect QuickBooks:', err);
    }

    setConnected(false);
    setQbUser(null);
  }

  async function handleAutoCreateChange(e) {
    const nextValue = e.target.checked;
    if (!userId) return;

    setSavingAutoCreate(true);
    try {
      const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}/quickbooks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          autoCreateOnSave: nextValue,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to save QuickBooks settings.');
      }

      setAutoCreateOnSave(nextValue);
    } catch (err) {
      console.error('Failed to save QuickBooks auto-create setting:', err);
    } finally {
      setSavingAutoCreate(false);
    }
  }

  function formatSyncStartDate(value) {
    // Parsed as local midnight so the date shown is the date picked.
    return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  async function saveSyncStartDate(nextValue) {
    if (!userId) return;

    setSavingSyncStartDate(true);
    setSyncStartDateMessage('');
    try {
      const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}/quickbooks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ syncStartDate: nextValue || null }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to save the QuickBooks sync start date.');
      }

      setSyncStartDate(nextValue || '');
      setSavedSyncStartDate(nextValue || '');
      setSyncStartDateMessage(
        nextValue
          ? `Saved. Anything dated before ${formatSyncStartDate(nextValue)} will not be sent to QuickBooks.`
          : 'Sync start date removed. Records of any date can sync to QuickBooks.'
      );
    } catch (err) {
      setSyncStartDateMessage(err.message || 'Failed to save the QuickBooks sync start date.');
    } finally {
      setSavingSyncStartDate(false);
    }
  }

  async function handleRetainerLiabilityAccountSave() {
    if (!userId) return;

    const normalizedSelectedAccountId = String(selectedLiabilityAccountId || '').trim();
    const fallbackAccountName = normalizedSelectedAccountId
      ? ''
      : String(manualLiabilityAccountName || '').trim();
    const selectedAccount =
      fallbackAccountName
        ? null
        : liabilityAccounts.find((account) => account.id === normalizedSelectedAccountId) || null;

    setSavingLiabilityAccount(true);
    try {
      const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}/quickbooks`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          retainerLiabilityAccountId: fallbackAccountName ? null : selectedAccount?.id || null,
          retainerLiabilityAccountName:
            selectedAccount?.fullyQualifiedName || selectedAccount?.name || fallbackAccountName || null,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to save QuickBooks liability account.');
      }
    } catch (err) {
      console.error('Failed to save QuickBooks liability account:', err);
    } finally {
      setSavingLiabilityAccount(false);
    }
  }

  async function handleBackfillPoPayments() {
    const token = getToken();
    if (!token || !connected || backfillingPoPayments) return;

    setBackfillingPoPayments(true);
    setBackfillMessage('');
    try {
      const res = await fetch(`${apiUrl}/api/quickbooks/backfill-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          includeInvoices: false,
          includeTimeBilling: false,
          includePurchaseOrders: true,
          dryRun: false,
          maxDocuments: 1000,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to backfill purchase-order bill payments.');
      }

      const summary = data?.summary || {};
      const documents = Array.isArray(summary.documents) ? summary.documents : [];
      const failedDocs = documents.filter((doc) => doc?.status === 'partial');
      const skippedDocs = documents.filter((doc) => doc?.status === 'skipped');
      const sampleIssues = [...failedDocs, ...skippedDocs]
        .slice(0, 3)
        .map((doc) => `${doc?.label || 'PO'} (${doc?.reason || 'partial_failure'})`)
        .join('; ');
      setBackfillMessage(
        `PO payment backfill complete. Synced ${Number(summary.syncedPayments || 0)} payments across ${Number(summary.updatedDocuments || 0)} purchase orders.${sampleIssues ? ` Issues: ${sampleIssues}.` : ''}`
      );
    } catch (err) {
      setBackfillMessage(err.message || 'Failed to run PO payment backfill.');
    } finally {
      setBackfillingPoPayments(false);
    }
  }

  async function handleResetStaleLinks() {
    const token = getToken();
    if (!token || !connected || resettingLinks) return;

    const confirmed = window.confirm(
      "This clears QuickBooks links (estimate/invoice/bill IDs) on proposals, invoices, and purchase orders that were synced under a previous QuickBooks company. Use this if you recently connected InteriorFolio to a different QuickBooks company and your existing data isn't syncing. After resetting, use each record's \"Sync to QuickBooks\" action to push it to the current company. Continue?"
    );
    if (!confirmed) return;

    setResettingLinks(true);
    setResetLinksMessage('');
    try {
      const res = await fetch(`${apiUrl}/api/quickbooks/resolve-sync-issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ issueType: 'company-switch' }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || 'Failed to reset QuickBooks links.');
      }

      const unlinked = data?.unlinked || {};
      const unlinkedCount = Object.values(unlinked).reduce((sum, n) => sum + (Number(n) || 0), 0);
      setResetLinksMessage(
        unlinkedCount > 0
          ? `Cleared ${unlinkedCount} old QuickBooks link(s). Open each proposal, invoice, or purchase order and sync it to push it to the current company.`
          : 'No stale QuickBooks links were found.'
      );
    } catch (err) {
      setResetLinksMessage(err.message || 'Failed to reset QuickBooks links.');
    } finally {
      setResettingLinks(false);
    }
  }

  const disconnectNote = connected ? null : describeQuickBooksDisconnect(disconnect);

  /* --------------------------------------------------------
     RENDER
  -------------------------------------------------------- */
  return (
    <div className="qb-settings-page">
      <h2 className="qb-title">QuickBooks Online</h2>
      <p className="qb-subtitle">Manage your QuickBooks integration.</p>

      <div className="qb-card">
        <div className="qb-left">
          {loading ? (
            <div className="qb-skeleton">
              <div className="sk-row sk-title"></div>
              <div className="sk-row sk-line"></div>
              <div className="sk-btn"></div>
            </div>
          ) : (
            <>
              <div className="qb-status-row">
                <span className={`qb-dot ${connected ? 'on' : 'off'}`}></span>
                <span className="qb-status-text">
                  {connected
                    ? `Connected to QuickBooks${qbEnvironment ? ` (${qbEnvironment === 'production' ? 'Production' : 'Sandbox'})` : ''}`
                    : 'Not Connected'}
                </span>
              </div>

              {connected && qbUser?.email && (
                <div className="qb-info">
                  Connected as <strong>{qbUser.email}</strong>
                </div>
              )}

              {disconnectNote && <div className="qb-info">{disconnectNote}</div>}

              {!connected ? (
                <button
                  className="qb-button connect qb-pulse"
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? <span className="qb-spinner"></span> : 'Connect to QuickBooks'}
                </button>
              ) : (
                <button className="qb-button disconnect" onClick={handleDisconnect}>
                  Disconnect QuickBooks
                </button>
              )}

              <div style={{ marginTop: 18 }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    opacity: connected ? 1 : 0.65,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={autoCreateOnSave}
                    disabled={!connected || savingAutoCreate}
                    onChange={handleAutoCreateChange}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong>Auto-sync to QuickBooks on save</strong>
                    <br />
                    Automatically create new proposals, invoices, and purchase-order bills in
                    QuickBooks after save, and update already-linked records on later saves.
                  </span>
                </label>
              </div>

              <div style={{ marginTop: 18 }}>
                <label
                  htmlFor="qb-sync-start-date"
                  style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}
                >
                  Sync Start Date
                </label>
                <div style={{ fontSize: 13, marginBottom: 8, color: '#5d6470' }}>
                  Nothing dated before this date is sent to QuickBooks, so books already kept
                  there (for example, entered from a previous system) aren't duplicated. That
                  covers proposals, invoices, purchase orders, payments, deposits, retainers, and
                  credits, plus any payment on a document dated before it. Leave blank to sync
                  everything.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                  <input
                    id="qb-sync-start-date"
                    type="date"
                    value={syncStartDate}
                    disabled={savingSyncStartDate}
                    onChange={(e) => {
                      setSyncStartDate(e.target.value);
                      setSyncStartDateMessage('');
                    }}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid #d6dae1',
                      background: '#fff',
                    }}
                  />
                  <button
                    className="qb-button connect"
                    onClick={() => saveSyncStartDate(syncStartDate)}
                    disabled={savingSyncStartDate || syncStartDate === savedSyncStartDate}
                  >
                    {savingSyncStartDate ? 'Saving...' : 'Save Start Date'}
                  </button>
                  {savedSyncStartDate && (
                    <button
                      className="qb-button disconnect"
                      onClick={() => saveSyncStartDate('')}
                      disabled={savingSyncStartDate}
                    >
                      Remove
                    </button>
                  )}
                </div>
                {syncStartDateMessage && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#364152' }}>
                    {syncStartDateMessage}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 18, opacity: connected ? 1 : 0.65 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                  Connected to a Different QuickBooks Company?
                </label>
                <div style={{ fontSize: 13, marginBottom: 8, color: '#5d6470' }}>
                  If you reconnected InteriorFolio to a different QuickBooks company, existing
                  proposals, invoices, and purchase orders still point at the old company and
                  won't sync. This clears those old links so each can be synced fresh to the
                  company you're connected to now.
                </div>
                <button
                  className="qb-button connect"
                  onClick={handleResetStaleLinks}
                  disabled={!connected || resettingLinks}
                >
                  {resettingLinks ? 'Resetting...' : 'Reset QuickBooks Links'}
                </button>
                {resetLinksMessage && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#364152' }}>{resetLinksMessage}</div>
                )}
              </div>

              <div style={{ marginTop: 18, opacity: connected ? 1 : 0.65 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                  Purchase Order Bill Payments
                </label>
                <div style={{ fontSize: 13, marginBottom: 8, color: '#5d6470' }}>
                  Backfill missing QuickBooks bill payments from InteriorFolio PO paid amounts.
                </div>
                <button
                  className="qb-button connect"
                  onClick={handleBackfillPoPayments}
                  disabled={!connected || backfillingPoPayments}
                >
                  {backfillingPoPayments ? 'Backfilling...' : 'Backfill PO Bill Payments'}
                </button>
                {backfillMessage && (
                  <div style={{ marginTop: 8, fontSize: 13, color: '#364152' }}>{backfillMessage}</div>
                )}
              </div>

              <div style={{ marginTop: 18, opacity: connected ? 1 : 0.65 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                  Retainer Liability Account
                </label>
                <div style={{ fontSize: 13, marginBottom: 8, color: '#5d6470' }}>
                  Choose the QuickBooks account used for retainers and customer deposits.
                  Retainer-style liability accounts are shown first, and if they exist,
                  only those accounts are listed.
                </div>
                <select
                  value={selectedLiabilityAccountId}
                  disabled={!connected || loadingLiabilityAccounts || savingLiabilityAccount}
                  onChange={(e) => {
                    setSelectedLiabilityAccountId(e.target.value);
                    if (e.target.value) {
                      setManualLiabilityAccountName('');
                    }
                  }}
                  style={{
                    width: '100%',
                    maxWidth: 420,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid #d6dae1',
                    background: '#fff',
                  }}
                >
                  <option value="">
                    {loadingLiabilityAccounts
                      ? 'Loading liability accounts...'
                      : liabilityAccounts.length
                        ? 'Select liability account'
                        : 'No liability accounts found'}
                  </option>
                  {liabilityAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {`${account.fullyQualifiedName || account.name} (${account.accountSubType || account.accountType})`}
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 10 }}>
                  <button
                    className="qb-button connect"
                    onClick={handleRetainerLiabilityAccountSave}
                    disabled={
                      !connected ||
                      loadingLiabilityAccounts ||
                      savingLiabilityAccount ||
                      (!selectedLiabilityAccountId && !String(manualLiabilityAccountName || '').trim())
                    }
                  >
                    {savingLiabilityAccount ? 'Saving...' : 'Save Liability Account'}
                  </button>
                </div>
                <div style={{ marginTop: 12 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
                    Can't Find It?
                  </label>
                  <div style={{ fontSize: 13, marginBottom: 8, color: '#5d6470' }}>
                    Enter the exact QuickBooks account name manually, for example
                    {' '}<code>IF Liabilities</code>.
                  </div>
                  <input
                    type="text"
                    value={manualLiabilityAccountName}
                    disabled={!connected || savingLiabilityAccount}
                    onChange={(e) => {
                      setManualLiabilityAccountName(e.target.value);
                      if (e.target.value.trim()) {
                        setSelectedLiabilityAccountId('');
                      }
                    }}
                    placeholder="Exact QuickBooks account name"
                    style={{
                      width: '100%',
                      maxWidth: 420,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid #d6dae1',
                      background: '#fff',
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className="qb-right">
          <img src={qbLogo} alt="QuickBooks Logo" className="qb-logo" />
        </div>
      </div>
    </div>
  );
}
